import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Checkbox, Label, MessageModal } from 'common/components';
import {
    MAP_TO_VARIANT_ERROR_MESSAGE,
    MAP_TO_VARIANT_ERROR_MESSAGE_TITLE,
    MAP_TO_VARIANT_NEST_OR_INDICATOR,
    MAP_TO_VARIANT_NEST_OR_INDICATOR_TITLE,
    MAP_TO_VARIANT_NO_ITEM_ERROR_MESSAGE,
    MAP_TO_VARIANT_NO_ITEM_ERROR_MESSAGE_TITLE,
    MAP_TO_VARIANT_SELECT_CAT_SUBCAT_MESSAGE,
    MAP_TO_VARIANT_SELECT_CAT_SUBCAT_TITLE,
    MAP_TO_VARIANT_SUCCESS_MESSAGE,
    MAP_TO_VARIANT_SUCCESS_MESSAGE_TITLE
} from 'common/config';
import { EntityType, MessageType } from 'common/enums';
import { FieldError } from 'common/models';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { RootState } from 'common/redux/core/root.reducer';

import VariantAddAndMoveSubcategoryPopup from 'modules/master-data/components/variant-add-and-move-sub-category-popup/VariantAddAndMoveSubcategoryPopup';
import VariantWizardPopup from 'modules/master-data/components/variant-move-wizard-popup/VariantWizardPopup';
import HierarchyCheckboxList from 'modules/master-data/components/hierarchy-checkbox-list/HierarchyCheckboxList';
import VariantsHeader from 'modules/master-data/components/variants-header/VariantsHeader';
import { getMode } from 'modules/data-editor/redux/dataEditor.selector';
import { getHierarchyCheckboxListColDefs } from 'modules/master-data/config';
import { ButtonTitles, Mode } from 'modules/master-data/enums/mode.enum';
import { masterDataActions } from 'modules/master-data/redux';
import {
    getFieldErrors,
    getHierarchicalDataSelector,
    getSelectedVariantsArray
} from 'modules/master-data/redux/masterData.selector';

import './Variants.scss';

const Variants: FC = () => {
    const dispatch = useDispatch();

    const [pagedRequest, setPagedRequest] = useState<PagedRequest>({
        pageNumber: 1,
        pageSize: 10,
        searchText: ''
    });

    useEffect(() => {
        dispatch(masterDataActions.getHierarchicalData(pagedRequest));
    }, [dispatch, pagedRequest]);

    const navigate = useNavigate();

    /**
     * MoveWizardPopup state variables and functions
     */
    const [modalData, setModalData] = useState<any>(null);
    const [hierarchicalViewPopupFlag, setHierarchicalViewPopupFlag] = useState(false);
    const [addAndMoveSubCategoryFlag, setAddAndMoveSubCategoryFlag] = useState(false);

    const fieldErrors = useSelector((state: RootState) => getFieldErrors(state));

    const updateSelectedRadioButton = (entityId: string) => {
        dispatch(
            masterDataActions.setRadioButtonSelected({
                targetId: entityId
            })
        );
    };

    const updateFieldErrors = (errors: Array<FieldError>) => {
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    /**
     * Back button from create new subcategory popup
     */
    const onBackButtonClickedSubCategory = () => {
        setHierarchicalViewPopupFlag(true);
        setAddAndMoveSubCategoryFlag(false);
    };

    /**
     *
     */
    const navigateToMasterData = () => {
        setPopupDetails({
            title: MAP_TO_VARIANT_SUCCESS_MESSAGE_TITLE,
            text: MAP_TO_VARIANT_SUCCESS_MESSAGE + '"' + selectedButtonFlag + '".',
            type: 'SUCCESS',
            source: 'NEST'
        });
        setShowPopup(true);
        // navigate('/variants/master-data');
    };

    /**
     *
     * @param createNew
     * @param data
     */
    const saveHandler = (createNew: string, data: any) => {
        if (createNew == 'createNewSubCategory') {
            /**
             * Move subcategory/indicator newly created subcategory
             */
            setPopupDetails({
                title: MAP_TO_VARIANT_SUCCESS_MESSAGE_TITLE,
                text: MAP_TO_VARIANT_SUCCESS_MESSAGE + '"' + selectedButtonFlag + '".',
                type: 'SUCCESS',
                source: 'NEST'
            });
            setShowPopup(true);
            setAddAndMoveSubCategoryFlag(true);
            setHierarchicalViewPopupFlag(false);
        } else if (createNew == 'createNewCategory') {
            /**
             * Move subcategory/indicator newly created category and subcategory
             */
            setPopupDetails({
                title: MAP_TO_VARIANT_SUCCESS_MESSAGE_TITLE,
                text: MAP_TO_VARIANT_SUCCESS_MESSAGE + '"' + selectedButtonFlag + '".',
                type: 'SUCCESS',
                source: 'NEST'
            });
            setShowPopup(true);
        } else {
            /**
             * Move subcategory/Indicator between existing category or subcategory
             * Move subcategory/indicator newly created subcategory
             */
            setPopupDetails({
                title: MAP_TO_VARIANT_SUCCESS_MESSAGE_TITLE,
                text: MAP_TO_VARIANT_SUCCESS_MESSAGE + '"' + selectedButtonFlag + '".',
                type: 'SUCCESS',
                source: 'NEST'
            });
            setShowPopup(true);
            setHierarchicalViewPopupFlag(false);
        }
    };

    /**
     * Confirmation popup details state variable
     */
    const [popupDetails, setPopupDetails] = useState({
        title: '',
        text: '',
        type: 'move',
        source: ''
    });
    const [isShowPopup, setShowPopup] = useState<boolean>(false);

    /**
     * Fetching masterdata for hierarchical view
     */
    const hierarchicalData = useSelector((state: RootState) => getHierarchicalDataSelector(state));

    /**
     * State variable for selecting variant button
     */
    const [selectedButtonFlag, setSelectedButtonFlag] = useState('');

    /**
     * State variable to set select all check box state
     */
    const [selectAllFlag, setSelectAllFlag] = useState(false);

    /**
     * State variable to contain search text
     */
    const [searchVal, setSearchVal] = useState<string>('');

    const mode = useSelector((state: RootState) => getMode(state));

    /**
     * This function handle serach event
     * @param event Event information object created by user on search box
     */
    const onSearchChangeHandler = (event: any) => {
        setSearchVal(event.target.value);
        if (mode != Mode.DataLoaded) dispatch(masterDataActions.setMode(Mode.DataLoaded));
    };

    /**
     * Function will handle action on quit mapping button pressed
     * @returns true
     */
    const onQuitMapping = () => {
        resetSelectedVariants();
        navigate('/variants/master-data');
        return true;
    };

    /**
     * Following function will handle clicked event of check box
     * @param isSelected Checkbox status
     * @param data Master/Preview data array
     */
    const handleCheckBoxClicked = (isSelected: boolean, data: any) => {
        setModalData(data);
        dispatch(masterDataActions.gatherSelectedVariants({ isSelected, data }));

        dispatch(masterDataActions.setCheckboxSelected({ isSelected: false, data }));
    };

    /**
     * Following function will handle expantion toggle
     * @param rowData is the row which is toggle expantion
     */
    const handleExpansionToggle = (rowData: any) => {
        dispatch(masterDataActions.toggleHierarchicalDataExpansion(rowData));
    };

    /**
     * Following function will handle select all feature
     * @param isSelected State of select all check box
     */
    const onClickSelectAllCheckbox = (isSelected: boolean) => {
        setSelectAllFlag(isSelected);
        resetSelectedVariants();
        dispatch(masterDataActions.setCheckboxSelectAll({ isSelected }));
    };

    /**
     * Follwing function will be called on click of map now button
     * Getting selected variants data items.
     */
    const selectedVariantsArray = useSelector((state: RootState) =>
        getSelectedVariantsArray(state)
    );

    const mapNowButtonClicked = () => {
        let selectedNest = false;
        let selectedIndicator = false;

        if (selectedVariantsArray.length > 0) {
            selectedVariantsArray.map((item: any, index: any) => {
                if (
                    item?.entityType == EntityType.Category ||
                    item?.entityType == EntityType.SubCategory
                ) {
                    selectedNest = true;
                } else if (item?.entityType == EntityType.Indicator) {
                    selectedIndicator = true;
                }
            });
        } else {
            setPopupDetails({
                title: MAP_TO_VARIANT_NO_ITEM_ERROR_MESSAGE_TITLE,
                text: MAP_TO_VARIANT_NO_ITEM_ERROR_MESSAGE,
                type: 'ERROR',
                source: ''
            });
            setShowPopup(true);
        }

        if (selectedNest && selectedIndicator) {
            setPopupDetails({
                title: MAP_TO_VARIANT_NEST_OR_INDICATOR_TITLE,
                text: MAP_TO_VARIANT_NEST_OR_INDICATOR,
                type: 'ERROR',
                source: ''
            });
            setShowPopup(true);
        } else if (selectedButtonFlag == '') {
            setPopupDetails({
                title: MAP_TO_VARIANT_ERROR_MESSAGE_TITLE,
                text: MAP_TO_VARIANT_ERROR_MESSAGE,
                type: 'ERROR',
                source: ''
            });
            setShowPopup(true);
        } else if (selectedNest) {
            setPopupDetails({
                title: MAP_TO_VARIANT_SUCCESS_MESSAGE_TITLE,
                text: MAP_TO_VARIANT_SUCCESS_MESSAGE + '"' + selectedButtonFlag + '".',
                type: 'SUCCESS',
                source: selectedNest ? 'NEST' : selectedIndicator ? 'INDICATOR' : ''
            });
            setShowPopup(true);
        } else if (selectedIndicator) {
            setPopupDetails({
                title: MAP_TO_VARIANT_SELECT_CAT_SUBCAT_TITLE + selectedButtonFlag,
                text: MAP_TO_VARIANT_SELECT_CAT_SUBCAT_MESSAGE + selectedButtonFlag,
                type: 'move',
                source: selectedNest ? 'NEST' : selectedIndicator ? 'INDICATOR' : ''
            });
            setHierarchicalViewPopupFlag(true);
        }
    };

    /**
     * Reset selected variants function handle emptying array.
     */
    const resetSelectedVariants = () => {
        dispatch(masterDataActions.resetSelectedVariants());
    };

    return (
        <div className="variants-container">
            <VariantsHeader
                searchVal={searchVal}
                onSearchChangeHandler={onSearchChangeHandler}
                onQuitMapping={onQuitMapping}></VariantsHeader>
            <div className="variants-container__below-header-label">
                Select each and map to variant on the right:
            </div>
            <div className="variants-container__body-container">
                <div className="variants-container__body-container__left-section">
                    <div className="variants-container__body-container__left-section__select-all-check-box-container">
                        <Checkbox
                            name="selectAll"
                            id="selectAll"
                            checked={selectAllFlag}
                            value="selectAll"
                            className="variants-container__body-container__left-section__select-all-check-box-container__select-all-check-box"
                            onChange={(event) => {
                                onClickSelectAllCheckbox(event.target.checked);
                            }}></Checkbox>
                        <Label
                            id="selectAll"
                            text="Select all"
                            style={{
                                paddingLeft: '2.5rem',
                                fontSize: '1.2rem',
                                marginTop: '1.3rem'
                            }}></Label>
                    </div>
                    <div className="variants-container__body-container__left-section__master-data-tree-view">
                        <HierarchyCheckboxList
                            dataEditorMode={hierarchicalData.length === 0}
                            data={hierarchicalData}
                            columnDefs={getHierarchyCheckboxListColDefs(
                                handleCheckBoxClicked,
                                handleExpansionToggle
                            )}
                        />
                    </div>
                </div>
                <div className="variants-container__body-container__right-section">
                    <div className="variants-container__body-container__right-section__top-section">
                        <div className="variants-container__body-container__right-section__top-section__variant-lebel">
                            {'Variants:'}
                        </div>
                        <div className="variants-container__body-container__right-section__top-section__note-lebel">
                            {
                                "Select categories from left and then choose a variant from the below and click on 'Map now' button."
                            }
                        </div>
                        <Button
                            variant={
                                selectedButtonFlag == ButtonTitles.GBB_PUBLIC
                                    ? 'primary'
                                    : 'secondary'
                            }
                            className={`variants-container__body-container__right-section__top-section${
                                selectedButtonFlag == ButtonTitles.GBB_PUBLIC
                                    ? '__variant-as-button-selected'
                                    : '__variant-as-button'
                            }`}
                            onClick={() => setSelectedButtonFlag(ButtonTitles.GBB_PUBLIC)}>
                            {ButtonTitles.GBB_PUBLIC}
                        </Button>
                    </div>
                    {/**
                     * Do not remove this commented code these button will be enabled in future.
                     */
                    /* <Button
                        variant={selectedButtonFlag == 'GBB Subscription' ? 'primary' : 'secondary'}
                        className={`variants-container__body-container__right-section${
                            selectedButtonFlag == 'GBB Subscription'
                                ? '__variant-as-button-selected'
                                : '__variant-as-button'
                        }`}
                        onClick={() => setSelectedButtonFlag('GBB Subscription')}>
                        GBB Subscription
                    </Button> */}
                    {/* <Button
                        variant={selectedButtonFlag == 'GBB Lorem' ? 'primary' : 'secondary'}
                        className={`variants-container__body-container__right-section${
                            selectedButtonFlag == 'GBB Lorem'
                                ? '__variant-as-button-selected'
                                : '__variant-as-button'
                        }`}
                        onClick={() => setSelectedButtonFlag('GBB Lorem')}>
                        GBB Lorem
                    </Button> */}

                    <div className="variants-container__body-container__right-section__bottom-button-container">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                resetSelectedVariants();
                                navigate('/variants/master-data');
                            }}
                            className="variants-container__body-container__right-section__bottom-button-container__cancel-button">
                            {ButtonTitles.CANCEL}
                        </Button>
                        <Button
                            variant="primary"
                            className="variants-container__body-container__right-section__bottom-button-container__map-now-button"
                            disabled={selectedButtonFlag == ''}
                            onClick={mapNowButtonClicked}>
                            {ButtonTitles.MAP_NOW}
                        </Button>
                    </div>
                </div>
            </div>
            {isShowPopup && (
                <MessageModal
                    headerName={popupDetails.title}
                    message={popupDetails.text}
                    isVisible={isShowPopup}
                    type={
                        selectedButtonFlag == '' || popupDetails.type == 'ERROR'
                            ? MessageType.Error
                            : MessageType.Success
                    }
                    isSecondaryButtonVisible={false}
                    primaryButton={selectedButtonFlag == '' ? ButtonTitles.CANCEL : ButtonTitles.OK}
                    secondaryButton={ButtonTitles.CANCEL}
                    onPrimaryAction={() => {
                        if (popupDetails.type == 'SUCCESS') {
                            setShowPopup(false);
                            if (selectedButtonFlag !== '') {
                                if (popupDetails.source == 'NEST') {
                                    navigate('/variants/master-data');
                                    resetSelectedVariants();
                                } else if (popupDetails.source == 'INDICATOR') {
                                    /**
                                     * Will write here for indicator
                                     */
                                }
                            }
                        } else {
                            setShowPopup(false);
                        }
                    }}
                    onSecondaryAction={() => {
                        setShowPopup(false);
                    }}
                />
            )}

            {hierarchicalViewPopupFlag && (
                <VariantWizardPopup
                    entityId={modalData?.id}
                    entityType={modalData?.entityType}
                    isVisible={hierarchicalViewPopupFlag}
                    updateSelectedRadioButton={updateSelectedRadioButton}
                    closePopupHandler={() => setHierarchicalViewPopupFlag(false)}
                    modalDetails={popupDetails}
                    saveHandler={saveHandler}
                    updateFieldErrors={updateFieldErrors}
                    fieldErrors={fieldErrors}
                    hierarchicalData={hierarchicalData}
                    toggleExpansion={handleExpansionToggle}
                />
            )}

            {addAndMoveSubCategoryFlag && (
                <VariantAddAndMoveSubcategoryPopup
                    entityId={modalData?.id}
                    fieldErrors={fieldErrors}
                    updateFieldErrors={updateFieldErrors}
                    entityType={modalData?.entityType}
                    isVisible={addAndMoveSubCategoryFlag}
                    updateSelectedRadioButton={updateSelectedRadioButton}
                    onBackButtonClicked={onBackButtonClickedSubCategory}
                    navigateToMasterData={navigateToMasterData}
                    closePopupHandler={() => setAddAndMoveSubCategoryFlag(false)}
                    hierarchicalData={hierarchicalData}
                    toggleExpansion={handleExpansionToggle}
                />
            )}
        </div>
    );
};

export default Variants;
